import { Box, Divider } from '@chakra-ui/react';
import { Button, Colors, Text } from '@mercator-gcp/grout';
import { startCase, toLower } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { CaretLeftButton } from 'components/elements/Button';
import { StickyItem } from 'components/elements/StickyItem';
import { FlexRow } from 'components/layout/FlexRow';
import { TITLE_BAR_HEIGHT } from 'components/widgets/ProjectDetails/const';
import { useCurrentRegion } from 'state/currentRegion';
import { Project } from 'types';
import { ZOOM_THRESHOLD } from 'utils/constants';
import { METROPOLITAN_CODE_TO_STATE_CODE } from 'utils/constants/location';
import { formatZipCode } from 'utils/format';
import { buildProjectMapURLWithParams } from 'utils/navigation';

const stickyHeaderStyle = {
  zIndex: 3,
  height: TITLE_BAR_HEIGHT,
};

const topNavStyle = {
  row: {
    justifyContent: 'center',
    paddingX: 'padding_48',
    paddingY: 'padding_16',
    background: Colors.White,
  },
  headerInnerStyle: {
    justifyContent: 'center',
    maxWidth: '1440px',
    width: '100%',
    position: 'relative',
  },
};

const backButtonStyle = {
  position: 'absolute',
  left: '0',
  _hover: {
    textDecor: 'underline',
  },
};

const backButtonIconStyle = {
  background: 'white',
  color: 'brand.army-dark',
  _hover: {
    background: 'transparent !important',
  },
};

const titleContainer = {
  display: 'inline-flex',
  gap: (theme: any) => theme.space.eight,
};

const goToMapButtonStyle = {
  position: 'absolute',
  right: '0',
  _hover: {
    textDecor: 'underline',
  },
};

const dividerStyle = {
  borderColor: '#CCD1CC',
  borderWidth: '1px',
};

type TProps = {
  address: Project['address'];
  city: Project['city'];
  state: Project['state'];
  coordinates?: { lat: Project['latitude']; lng: Project['longitude'] };
  zipcode: Project['zipcode'];
};

const PageHeader = ({ address, city, state, coordinates, zipcode }: TProps) => {
  const navigate = useNavigate();
  const { currentRegion } = useCurrentRegion();
  const stateCode = currentRegion
    ? METROPOLITAN_CODE_TO_STATE_CODE[currentRegion]
    : undefined;

  const handleViewOnMapClick = () => {
    if (!coordinates) {
      return;
    }
    navigate(
      buildProjectMapURLWithParams({
        coordinates: `${coordinates.lat},${coordinates.lng}`,
        zoom: ZOOM_THRESHOLD,
        stateCode,
      }),
    );
  };

  const handleBackClick = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(
        buildProjectMapURLWithParams({
          stateCode,
        }),
      );
    }
  };

  return (
    <StickyItem sx={stickyHeaderStyle} position="sticky">
      <FlexRow sx={topNavStyle.row}>
        <FlexRow sx={topNavStyle.headerInnerStyle}>
          <Box sx={backButtonStyle}>
            <CaretLeftButton onClick={handleBackClick} sx={backButtonIconStyle}>
              Back
            </CaretLeftButton>
          </Box>
          <FlexRow sx={titleContainer}>
            <Text.Lead color={Colors.Black800}>{startCase(toLower(address))}</Text.Lead>
            <FlexRow>
              <Text color={Colors.TextQuiet}>
                {`${city} ${state}`}
                {zipcode ? `, ${formatZipCode(zipcode)}` : ''}
              </Text>
            </FlexRow>
          </FlexRow>
          {coordinates?.lat && coordinates?.lng && (
            <Button
              sx={goToMapButtonStyle}
              variant="unstyled"
              onClick={handleViewOnMapClick}
            >
              <Text.BodyBold>View more projects nearby</Text.BodyBold>
            </Button>
          )}
        </FlexRow>
      </FlexRow>
      <FlexRow>
        <Divider sx={dividerStyle} />
      </FlexRow>
    </StickyItem>
  );
};

export default PageHeader;
